import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'
import { openSignIn } from '@/lib/signin-navigation'

const MCP_LINK_URL =
  import.meta.env.VITE_BIDDLE_MCP_OAUTH_COMPLETE_URL?.replace(
    '/oauth/firebase-complete',
    '/oauth/link-firebase',
  ) ||
  'https://biddle-claude-mcp-831000579540.australia-southeast1.run.app/oauth/link-firebase'

const CODE_LENGTH = 6

/**
 * Step-up account link for the Claude MCP connector.
 * Connect itself is anonymous; this page only runs when Claude calls authenticate_biddle.
 *
 * Linking is never automatic. The user must confirm and type the pairing code
 * Claude showed them, so a link opened from elsewhere cannot bind this account
 * to someone else's connector session.
 */
export function McpOAuthPage() {
  const [searchParams] = useSearchParams()
  const pending = searchParams.get('pending')?.trim() || ''
  const mode = searchParams.get('mode')?.trim() || 'link'
  const done = searchParams.get('done') === '1'
  const { user, isGuest, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [error, setError] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [linked, setLinked] = useState(false)
  const [wantSignIn, setWantSignIn] = useState(false)

  useEffect(() => {
    if (done) return
    if (mode !== 'link') {
      setError(
        'Connecting Biddle in Claude no longer requires signing in here. Go back to Claude — you can explore addresses without an account. Sign in only when you create a Biddle or claim a property.',
      )
      return
    }
    if (isLoading) return
    if (!pending) {
      setError('Missing link session. Ask Claude to run authenticate_biddle again.')
      return
    }
    if (wantSignIn && (!user || isGuest)) {
      openSignIn(
        navigate,
        location,
        `/mcp-oauth?pending=${encodeURIComponent(pending)}&mode=link`,
      )
    }
  }, [isLoading, user, isGuest, pending, mode, done, wantSignIn, navigate, location])

  const normalizedCode = code.toUpperCase().replace(/[^A-Z0-9]/g, '')

  async function submitLink() {
    if (!user || isGuest || normalizedCode.length !== CODE_LENGTH) return
    setSubmitting(true)
    setError(null)
    try {
      const idToken = await user.getIdToken()
      const res = await fetch(MCP_LINK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ pending, idToken, code: normalizedCode }),
      })
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
      if (!res.ok || !body.ok) {
        setError(body.error || 'Could not link your Biddle account. Try again.')
        return
      }
      setLinked(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not link Biddle account')
    } finally {
      setSubmitting(false)
    }
  }

  if (done || linked) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <h1 className="text-xl font-bold text-navy">You&apos;re connected</h1>
        <p className="max-w-md text-sm text-muted">
          Your Biddle account is linked to Claude. Return to Claude and retry creating a Biddle,
          listing yours, or claiming a property.
        </p>
        <Button type="button" onClick={() => navigate('/search')}>
          Open Biddle
        </Button>
      </div>
    )
  }

  const signedIn = Boolean(user) && !isGuest

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      {isLoading || (wantSignIn && !signedIn) ? (
        <Spinner className="size-8 text-biddle-blue" />
      ) : null}
      <h1 className="text-xl font-bold text-navy">Link Biddle to Claude</h1>
      {error ? <p className="max-w-md text-sm text-destructive">{error}</p> : null}

      {!signedIn ? (
        <>
          <p className="max-w-md text-sm text-muted">
            Create a free account or sign in so Claude can submit Biddles and claims for you.
            Exploring addresses in Claude does not require this step.
          </p>
          {!wantSignIn ? (
            <Button type="button" onClick={() => setWantSignIn(true)}>
              Sign in or create account
            </Button>
          ) : null}
        </>
      ) : (
        <form
          className="flex w-full max-w-sm flex-col items-center gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            void submitLink()
          }}
        >
          <p className="text-sm text-muted">
            Allow Claude to create Biddles and start ownership claims as you? Enter the pairing code
            Claude showed you to confirm this request came from your chat.
          </p>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Pairing code"
            autoComplete="one-time-code"
            inputMode="text"
            maxLength={12}
            className="text-center text-lg tracking-[0.3em] uppercase"
            aria-label="Pairing code from Claude"
          />
          <Button
            type="submit"
            disabled={submitting || normalizedCode.length !== CODE_LENGTH}
            className="w-full"
          >
            {submitting ? 'Linking…' : 'Connect to Claude'}
          </Button>
          <p className="text-xs text-muted">
            If you did not start this in Claude, close this page — do not enter a code someone else
            sent you.
          </p>
        </form>
      )}
    </div>
  )
}
